import io from 'socket.io-client';
import feathers from '@feathersjs/client';
import { Container } from 'unstated';

import sentry from 'src/feathers/sentry';
import authentication from 'src/feathers/authentication';
import notifications from 'src/feathers/notifications';

// Remote services
import accounts from 'src/feathers/services/accounts';
import users from 'src/feathers/services/users';
import jobs from 'src/feathers/services/jobs';
import stages from 'src/feathers/services/stages';
import jobComments from 'src/feathers/services/job-comments';
import jobDocuments from 'src/feathers/services/job-documents';
import jobStats from 'src/feathers/services/job-stats';

// Google Services
import googleDistance from 'src/feathers/services/google/geocode';
import googleDirections from 'src/feathers/services/google/directions';
import googleGeocode from 'src/feathers/services/google/geocode';

const app = feathers();

// Because sentry catches all errors, configure it
// before anything else.
app.configure(sentry);

const socket = io(process.env.REACT_APP_API_URL, {
  transports: ['websocket'],
  forceNew: true
});

// https://github.com/feathersjs/feathers/issues/1532
socket.on('reconnect', function () {
  socket.sendBuffer = [];
});

app.configure(feathers.socketio(socket));

// const rest = feathers.rest(process.env.REACT_APP_API_URL);
// app.configure(rest.fetch(window.fetch));

app.configure(
  feathers.authentication({
    storage: localStorage,
    storageKey: '6thman-token'
  })
);
app.configure(authentication);

app.configure(notifications);

// App state container
app.configure((app) => {
  class StateContainer extends Container {
    state = {
      unseenComments: [],
      mountedChats: []
    };
  }
  app.set('stateContainer', new StateContainer());
});

app.configure((app) => {
  app.prep = async function () {
    const [user, account] = await Promise.all([
      app.service('api/users').get(null),
      app.service('api/accounts').get(null)
    ]);
    const { data: unseenComments } = await app
      .service('api/job-comments')
      .find({
        query: {
          user_id: { $ne: user._id },
          seen_by: { $ne: user._id },
          $sort: { created_at: -1 }
        }
      });
    app
      .get('stateContainer')
      .setState({ user, account, unseenComments, prepped: true });
  };
});

app.configure((app) => {
  app.cleanup = async function (redirect) {
    if (app.authentication) {
      await app.authentication.removeAccessToken();
      await app.authentication.reset();
    }
    if (redirect) {
      window.location.href = `/login?redirect=${redirect}`;
    } else {
      window.location.href = '/login';
    }
  };
});

app.configure((app) => {
  const commentsService = app.service('api/job-comments');

  commentsService.on('created', (comment) => {
    const stateContainer = app.get('stateContainer');
    const { state } = stateContainer;
    // TODO: Technically a comment could be created by another
    // user with this user alreadys set in seen_by. But it is unlikely.
    if (comment.user_id !== state.user._id) {
      if (state.mountedChats.includes(comment.job_id)) {
        commentsService.patch(comment._id, {
          $push: { seen_by: state.user._id }
        });
      } else {
        stateContainer.setState({
          unseenComments: [comment, ...state.unseenComments]
        });
      }
    }
  });

  commentsService.on('patched', (data) => {
    data = Array.isArray(data) ? data : [data];
    const stateContainer = app.get('stateContainer');
    const { state } = stateContainer;
    const seen = data.reduce((seen, comment) => {
      if (comment.seen_by.includes(state.user._id)) {
        return [...seen, comment._id];
      } else {
        return seen;
      }
    }, []);
    if (seen.length) {
      stateContainer.setState({
        unseenComments: state.unseenComments.filter(
          (comment) => !seen.includes(comment._id)
        )
      });
    }
  });
});

// Configure mixins.
// https://github.com/fridays/feathers-findone/blob/master/index.js
app.configure((app) => {
  const makeParams = (params = {}) => {
    params.query = params.query || {};
    params.query.$limit = 1;
    params.skipHooks = [...params.skipHooks, 'setStore'];
    return params;
  };
  const getResult = (result) => {
    const data = result.data || result;
    return Array.isArray(data) ? data[0] : data;
  };

  app.methods = [...app.methods, 'findOne'];
  app.mixins.push(function (service) {
    service.findOne = function (params) {
      return service.find(makeParams(params)).then(getResult);
    };
  });
});

app.configure((app) => {
  app.mixins.push((service) => {
    service.timeout = 1000 * 10;
  });
});

app.configure(accounts);
app.configure(users);
app.configure(jobs);
app.configure(stages);
app.configure(jobComments);
app.configure(jobDocuments);
app.configure(jobStats);
app.configure(googleDistance);
app.configure(googleDirections);
app.configure(googleGeocode);

// After all calls, if is not authed, delete the token
// and redirect to the login page with a full page reload
// to purge any data in memory.
const redirectUnAuthed = async (context) => {
  if (context.error.code === 401 && context.path !== 'authentication') {
    await context.app.cleanup();
  }
  return context;
};

const getNullId = (context) => {
  if (context.app.rest !== undefined) {
    if (context.method === 'get' && context.id === null) {
      context.id = 'null';
    }
  }
  return context;
};

// Hooks run on all services
app.hooks({
  before: {
    get: [getNullId]
  },
  error: { all: [redirectUnAuthed] }
});

// Call all the underlying services' .setup() method.
app.setup();

export default app;
